import { Button } from "@/components/button"
import { DateField } from "@/components/custom/date-field"
import { HERO_COLORS } from "@/components/guild-war/team-colors"
import { RefreshButton } from "@/components/shared/refresh-button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { Spinner } from "@/components/ui/spinner"

type PublishedSummary = {
  state: "empty" | "loading" | "ready"
  text: string
}

export function RosterHeroCard({
  publishedSummary,
  selectedRosterDate,
  onDateChange,
  showExportButton,
  onShowExportPreview,
  isFetching,
  onRefresh,
}: {
  publishedSummary: PublishedSummary
  selectedRosterDate: string
  onDateChange: (value: string | undefined) => void
  showExportButton: boolean
  onShowExportPreview: () => void
  isFetching: boolean
  onRefresh: () => void
}) {
  return (
    <Card className="overflow-hidden border-border/60 bg-[radial-gradient(circle_at_top_left,rgba(190,223,255,0.18),transparent_28%),linear-gradient(180deg,#364955,#2c3c47)] text-foreground shadow-[0_26px_80px_-38px_rgba(0,0,0,0.85)]">
      <CardHeader className="gap-4 border-b border-foreground/10">
        <div className="grid gap-4 lg:grid-cols-[1.2fr_0.8fr] lg:items-end">
          <div className="space-y-2">
            <CardTitle
              className={`font-serif text-4xl tracking-tight ${HERO_COLORS.title} md:text-5xl`}
            >
              Published Guild War Roster
            </CardTitle>
            <CardDescription className="max-w-2xl text-sm text-muted-foreground md:text-base">
              Review the latest published lineup for the selected week. This
              page only shows roster snapshots that admins have explicitly
              published.
            </CardDescription>
          </div>

          <div className="grid gap-3">
            <div className="grid gap-1.5">
              <div className="flex items-center gap-2">
                <Label className="text-muted-foreground">War Date</Label>
                <RefreshButton
                  isFetching={isFetching}
                  onClick={onRefresh}
                  className="text-muted-foreground hover:text-foreground"
                />
              </div>
              <DateField
                value={selectedRosterDate}
                onValueChange={onDateChange}
                buttonClassName="w-full border-foreground/20 bg-foreground/8 text-foreground"
                className="border-foreground/20 bg-background/95 text-foreground"
              />
            </div>

            <div className="rounded-2xl border border-foreground/10 bg-foreground/8 px-4 py-3 text-sm text-foreground">
              {publishedSummary.state === "loading" ? (
                <span className="inline-flex items-center gap-2 text-foreground/90">
                  <Spinner className="size-4 text-muted-foreground" />
                  {publishedSummary.text}
                </span>
              ) : (
                publishedSummary.text
              )}
            </div>

            {showExportButton ? (
              <Button
                variant="outline"
                className={HERO_COLORS.exportButton}
                onClick={onShowExportPreview}
              >
                Preview Export Image
              </Button>
            ) : null}
          </div>
        </div>
      </CardHeader>
    </Card>
  )
}
